import type { SiteLang } from "@/hooks/useLanguage";

/* ─── Spanish translations for Lab Services offering pages ─── */

export interface LabOfferingI18n {
  title: string;
  subtitle: string;
  description: string;
  faqItems: { q: string; a: string }[];
  /* Section-level overrides */
  whatTitle: string;
  whatParagraphs: string[];
  whyTitle: string;
  whyBenefits: { title: string; text: string }[];
  whyIntro?: string;
  helpCards: { heading: string; intro: string; cards: { title: string; text: string }[] };
  processTitle: string;
  processIntro: string;
  processSteps: { title: string; text: string }[];
  partnerTitle: string;
  partnerIntro: string;
  partnerBullets: string[];
}

const labOfferingsEs: Record<string, LabOfferingI18n> = {

  /* ───────── Análisis químico de control de calidad ───────── */
  "chemical-analysis": {
    title: "Análisis químico de control de calidad",
    subtitle: "Ensayos analíticos integrales para tus necesidades farmacéuticas",
    description: "El laboratorio QC certificado GMP de QbD Group ofrece servicios fiables y precisos de análisis químico para materias primas, principios activos, IMP y productos medicinales terminados.",
    faqItems: [
      { q: "¿Qué tipos de análisis químico ofrecéis?", a: "Realizamos ensayos de identificación (IR, TLC, HPLC), ensayos de valoración (UV/Vis, titulación automática, HPLC/UHPLC, GC), uniformidad de dosis, ensayos de impurezas, determinaciones físicas y ensayos especializados como DSC y distribución de tamaño de partícula por láser." },
      { q: "¿Vuestro laboratorio está certificado GMP?", a: "Sí, nuestro laboratorio dispone de licencia GMP, certificado ISO 17025 y está auditado por la FAGG belga." },
      { q: "¿Podéis analizar productos de alta potencia?", a: "Sí, nuestro laboratorio está autorizado para trabajar con psicotrópicos, narcóticos, hormonas y productos de alta potencia." },
      { q: "¿Cuál es el plazo de entrega típico?", a: "Nos esforzamos por ofrecer tiempos de respuesta eficientes, adaptados a la complejidad del ensayo. Los plazos concretos se acuerdan durante la consulta inicial." },
    ],
    whatTitle: "¿Qué es el análisis químico de control de calidad?",
    whatParagraphs: [
      "El análisis químico de control de calidad (QC) comprende una serie de <strong class='text-foreground'>ensayos y procedimientos analíticos para verificar la calidad, seguridad y eficacia de los productos farmacéuticos</strong>. Desde materias primas hasta formas farmacéuticas terminadas, el análisis químico QC garantiza el cumplimiento de los estrictos estándares regulatorios y contribuye a mantener la integridad del producto.",
      "Los aspectos clave incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Ensayos farmacopeicos</strong>: verificación del cumplimiento con estándares de farmacopea.</li><li><strong>Productos terminados</strong>: ensayos de conformidad con el dossier regulatorio.</li><li><strong>Instrumentación avanzada</strong>: tecnología de última generación para resultados precisos.</li><li><strong>Cumplimiento normativo</strong>: métodos alineados con GMP y otros requisitos.</li></ul>",
    ],
    whyTitle: "¿Por qué es importante el análisis químico QC?",
    whyBenefits: [
      { title: "Seguridad del producto", text: "Garantiza que los productos farmacéuticos estén libres de impurezas perjudiciales." },
      { title: "Cumplimiento normativo", text: "Cumple con los estándares GMP y los requisitos farmacopeicos." },
      { title: "Quality Assurance", text: "Confirma la calidad y consistencia de las materias primas y los productos terminados." },
      { title: "Eficiencia operativa", text: "Reduce los riesgos de retiradas y no conformidades al identificar problemas de forma temprana." },
      { title: "Validación de procesos", text: "Soporta procesos de fabricación robustos con datos fiables." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "Nuestro laboratorio QC ofrece una amplia gama de servicios de análisis químico, incluyendo:",
      cards: [
        { title: "Ensayos de identificación", text: "IR, TLC y HPLC con múltiples detectores." },
        { title: "Ensayos de apariencia", text: "Evaluación de color y claridad." },
        { title: "Valoraciones", text: "Utilizando UV/Vis, titulación automática, HPLC/UHPLC y técnicas de GC." },
        { title: "Uniformidad de dosificación", text: "Garantizando una dosificación consistente en los productos medicinales." },
        { title: "Ensayos de impurezas", text: "Cuantificación de sustancias relacionadas, disolventes residuales e impurezas elementales." },
        { title: "Determinaciones físicas", text: "Medición de pH, viscosidad, densidad y más." },
        { title: "Ensayos de formas sólidas orales", text: "Incluyendo disgregación, disolución, dureza y friabilidad." },
        { title: "Ensayos especializados", text: "DSC, análisis TOC, distribución de tamaño de partícula por láser y evaluación de materia particulada." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Seguimos un enfoque estructurado para ofrecer resultados analíticos fiables y conformes con GMP.",
    processSteps: [
      { title: "1. Consulta", text: "Comprendemos tus necesidades analíticas y requisitos del producto." },
      { title: "2. Selección del método", text: "Elegimos el enfoque analítico óptimo para tus muestras." },
      { title: "3. Análisis", text: "Ejecutamos los ensayos con instrumentación avanzada en condiciones GMP." },
      { title: "4. Informe", text: "Entregamos resultados completos con documentación conforme a la normativa." },
    ],
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Al colaborar con QbD Group para análisis químico QC, obtienes:",
    partnerBullets: [
      "Experiencia integral — desde identificación general hasta ensayos avanzados de impurezas, cubrimos un amplio espectro de necesidades analíticas.",
      "Certificación GMP — laboratorio autorizado para trabajar con psicotrópicos, narcóticos, hormonas y productos de alta potencia.",
      "Equipamiento avanzado — tecnología de última generación para ensayos precisos y eficientes.",
      "Soporte flexible — análisis de servicio completo para que te concentres en tu negocio principal.",
      "Garantía regulatoria — servicios diseñados para cumplir con los estándares de cumplimiento globales.",
    ],
  },

  /* ───────── Desarrollo de métodos ───────── */
  "method-development": {
    title: "Desarrollo de métodos",
    subtitle: "Soluciones a medida para procedimientos analíticos precisos",
    description: "QbD Group combina una profunda experiencia científica con enfoques innovadores para diseñar métodos analíticos robustos adaptados a tus necesidades específicas.",
    faqItems: [
      { q: "¿Qué incluye el desarrollo de métodos?", a: "Incluye el diseño, la optimización y la documentación de procedimientos analíticos adaptados a tu producto, garantizando que sean aptos para su propósito y estén listos para la validación." },
      { q: "¿Podéis desarrollar métodos para productos complejos?", a: "Sí, nuestros científicos tienen experiencia con matrices complejas, formulaciones novedosas y retos analíticos específicos de la industria." },
      { q: "¿Cuánto tarda el desarrollo de un método?", a: "Los plazos dependen de la complejidad. Los métodos sencillos pueden estar listos en pocas semanas, mientras que los proyectos más complejos pueden requerir más tiempo. Proporcionamos una estimación clara durante la consulta." },
    ],
    whatTitle: "¿Qué es el desarrollo de métodos?",
    whatParagraphs: [
      "El desarrollo de métodos consiste en <strong class='text-foreground'>diseñar y optimizar procedimientos analíticos</strong> para evaluar con precisión la calidad, seguridad y eficacia de los productos farmacéuticos. Este proceso asegura que los métodos sean adecuados para su finalidad y capaces de ofrecer resultados reproducibles.",
      "Los aspectos clave incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Personalización</strong>: métodos diseñados para abordar retos específicos.</li><li><strong>Optimización</strong>: ajuste de parámetros para mejorar el rendimiento y la eficiencia.</li><li><strong>Diseño Fit-for-Purpose</strong>: métodos adaptados a las necesidades de la aplicación.</li><li><strong>Preparación para la validación</strong>: métodos robustos listos para su validación.</li></ul>",
    ],
    whyTitle: "¿Por qué es importante el desarrollo de métodos?",
    whyBenefits: [
      { title: "Quality Assurance", text: "Garantiza resultados fiables y precisos para análisis críticos." },
      { title: "Cumplimiento normativo", text: "Prepara los métodos para cumplir con estándares y directrices globales." },
      { title: "Eficiencia", text: "Reduce tiempos y costes mediante procedimientos analíticos optimizados." },
      { title: "Integridad del producto", text: "Protege la calidad y seguridad del producto en cada fase." },
      { title: "Confianza en los datos", text: "Proporciona una base sólida para la validación y los ensayos rutinarios." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "En QbD Group, ofrecemos soporte integral para el desarrollo de métodos:",
      cards: [
        { title: "Enfoques a medida", text: "Diseño de métodos específicos para los requisitos de tu producto y proceso." },
        { title: "Tecnología de última generación", text: "Instrumentación avanzada para resultados precisos." },
        { title: "Resolución de problemas", text: "Abordamos retos con soluciones creativas y eficaces." },
        { title: "Guía previa a la validación", text: "Preparamos los métodos para una validación e implementación sin contratiempos." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Seguimos un enfoque sistemático para desarrollar métodos analíticos robustos.",
    processSteps: [
      { title: "1. Análisis de requisitos", text: "Comprendemos tu producto, proceso y contexto regulatorio." },
      { title: "2. Diseño del método", text: "Desarrollamos una estrategia analítica optimizada adaptada a tus necesidades." },
      { title: "3. Optimización", text: "Ajustamos los parámetros del método para maximizar el rendimiento y la fiabilidad." },
      { title: "4. Documentación y entrega", text: "Documentación completa lista para la validación e implementación." },
    ],
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Colaborar con QbD Group para el desarrollo de métodos te ofrece:",
    partnerBullets: [
      "Excelencia científica — profunda experiencia en química analítica y ciencias farmacéuticas.",
      "Soluciones personalizadas — enfoques únicos adaptados a los objetivos de tu proyecto.",
      "Herramientas innovadoras — acceso a tecnología y metodologías de última generación.",
      "Colaboración estrecha — cooperación cercana para satisfacer tus necesidades específicas.",
    ],
  },

  /* ───────── Validación del método ───────── */
  "method-validation": {
    title: "Validación del método",
    subtitle: "Garantizando procedimientos analíticos fiables y conformes",
    description: "QbD Group asegura que tus métodos analíticos estén alineados con estándares globales como ICH Q2(R1) y directrices farmacopeicas, ofreciendo resultados precisos y reproducibles.",
    faqItems: [
      { q: "¿Qué directrices seguís para la validación de métodos?", a: "Seguimos las directrices ICH Q2(R1), estándares farmacopeicos y protocolos específicos del cliente para garantizar el cumplimiento regulatorio." },
      { q: "¿Qué parámetros evaluáis?", a: "Evaluamos exactitud, precisión, especificidad, linealidad, rango, límite de detección, límite de cuantificación y robustez." },
      { q: "¿Proporcionáis informes listos para presentación regulatoria?", a: "Sí, nuestros informes de validación están diseñados para cumplir con los requisitos de presentación ante autoridades regulatorias." },
    ],
    whatTitle: "¿Qué es la validación de métodos?",
    whatParagraphs: [
      "La validación de métodos es un <strong class='text-foreground'>proceso sistemático de evaluación de métodos analíticos</strong> para garantizar que ofrecen de forma consistente resultados fiables y precisos. Este proceso verifica que los métodos cumplen criterios predefinidos y son aptos para la presentación regulatoria y el uso rutinario.",
      "Los aspectos clave incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Alineación regulatoria</strong>: conforme a ICH Q2(R1), farmacopeas y directrices específicas del cliente.</li><li><strong>Evaluación de parámetros</strong>: exactitud, precisión, especificidad, linealidad y robustez.</li><li><strong>Documentación</strong>: informes completos de los estudios de validación.</li><li><strong>Transferibilidad</strong>: métodos listos para una transferencia fluida.</li></ul>",
    ],
    whyTitle: "¿Por qué es importante la validación de métodos?",
    whyBenefits: [
      { title: "Aprobación regulatoria", text: "Garantiza el cumplimiento de estándares internacionales y facilita el acceso al mercado." },
      { title: "Integridad del producto", text: "Confirma la calidad, seguridad y eficacia de los productos farmacéuticos." },
      { title: "Eficiencia operativa", text: "Reduce los riesgos de no conformidad y mejora la fiabilidad analítica." },
      { title: "Mitigación de riesgos", text: "Identifica y aborda posibles debilidades en los métodos analíticos." },
      { title: "Preparación para ensayos rutinarios", text: "Prepara los métodos para su aplicación consistente en control de calidad." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "En QbD Group, ofrecemos servicios integrales de validación de métodos:",
      cards: [
        { title: "Experiencia regulatoria", text: "Conforme a ICH Q2(R1) y directrices relevantes." },
        { title: "Validación completa", text: "Evaluación de todos los parámetros críticos, incluyendo exactitud, precisión y robustez." },
        { title: "Protocolos a medida", text: "Desarrollo y ejecución de protocolos de validación específicos para tus necesidades." },
        { title: "Informes de validación", text: "Documentación detallada para presentación regulatoria." },
        { title: "Preparación para transferencia", text: "Métodos validados listos para transferir a laboratorios externos." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Aseguramos un flujo de trabajo de validación exhaustivo y conforme.",
    processSteps: [
      { title: "1. Desarrollo del protocolo", text: "Creación de protocolos de validación alineados con estándares regulatorios." },
      { title: "2. Ejecución", text: "Realización de los experimentos de validación con documentación rigurosa." },
      { title: "3. Análisis de datos", text: "Evaluación de resultados frente a criterios de aceptación predefinidos." },
      { title: "4. Informe y transferencia", text: "Entrega de informes completos listos para presentación regulatoria." },
    ],
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Colaborar con QbD Group para la validación de métodos te ofrece:",
    partnerBullets: [
      "Conocimiento regulatorio — amplia experiencia con requisitos regulatorios globales.",
      "Resultados precisos — procesos de validación rigurosos para asegurar la fiabilidad del método.",
      "Soluciones a medida — protocolos personalizados para las necesidades únicas de tu proyecto.",
      "Soporte de principio a fin — asistencia integral desde la planificación de la validación hasta la elaboración del informe.",
    ],
  },

  /* ───────── Estudios de estabilidad ───────── */
  "stability-studies": {
    title: "Estudios de estabilidad",
    subtitle: "Protege la integridad y la vida útil de tus productos",
    description: "QbD Group asegura que tus productos cumplan los más altos estándares de calidad mediante rigurosos estudios de estabilidad conformes con ICH, con instalaciones de última generación.",
    faqItems: [
      { q: "¿Qué tipos de estudios de estabilidad realizáis?", a: "Realizamos estudios de estabilidad a largo plazo, intermedios y acelerados conforme a ICH, así como ensayos de estrés, estudios de estabilidad en uso y ciclos de temperatura." },
      { q: "¿Cómo se almacenan las muestras?", a: "Nuestras instalaciones de almacenamiento de estabilidad conformes con ICH están equipadas con monitorización 24/7, control de acceso y sistemas de alarma para garantizar la integridad de tus muestras." },
      { q: "¿Podéis desarrollar métodos indicadores de estabilidad?", a: "Sí, ofrecemos desarrollo y validación de métodos indicadores de estabilidad para un seguimiento preciso de la degradación del producto." },
    ],
    whatTitle: "¿Qué son los estudios de estabilidad?",
    whatParagraphs: [
      "Los estudios de estabilidad son <strong class='text-foreground'>investigaciones científicas para determinar cómo varía la calidad de un producto con el tiempo</strong> bajo factores ambientales como temperatura, humedad, luz y pH. Estos estudios evalúan las propiedades físicas, químicas, microbiológicas y funcionales del producto a lo largo de su ciclo de vida.",
      "Los aspectos clave incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Ensayos de estabilidad ICH</strong>: estudios a largo plazo, intermedios y acelerados.</li><li><strong>Ensayos de estrés</strong>: evaluación de la respuesta del producto a condiciones extremas.</li><li><strong>Ensayos de estabilidad en uso</strong>: evaluación de la estabilidad durante las condiciones típicas de uso.</li><li><strong>Ciclos de temperatura</strong>: simulaciones de congelación-descongelación y transporte.</li><li><strong>Validación de métodos indicadores de estabilidad</strong>: seguimiento preciso de la degradación del producto.</li></ul>",
    ],
    whyTitle: "¿Por qué son esenciales los estudios de estabilidad?",
    whyBenefits: [
      { title: "Cumplimiento normativo", text: "Garantiza que tu producto cumpla con los estrictos requisitos de las autoridades sanitarias." },
      { title: "Determinación de vida útil", text: "Ayuda a establecer las condiciones de almacenamiento adecuadas y las fechas de caducidad." },
      { title: "Quality Assurance", text: "Confirma que tu producto permanece estable bajo condiciones específicas." },
      { title: "Mitigación de riesgos", text: "Identifica posibles problemas de estabilidad durante el desarrollo." },
      { title: "Confianza del mercado", text: "Garantiza a clientes y partes interesadas la calidad de tu producto." },
      { title: "Soporte para mercados globales", text: "Permite el cumplimiento con directrices internacionales como ICH." },
      { title: "Eficiencia en costes", text: "Identifica problemas potenciales de forma temprana para evitar retiradas o reprocesos costosos." },
      { title: "Decisiones basadas en datos", text: "Optimiza formulación, envasado y condiciones de almacenamiento basándote en datos de estabilidad." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "Ofrecemos una suite completa de servicios de estudios de estabilidad adaptados a tus necesidades. Nuestras instalaciones de almacenamiento de estabilidad conformes con ICH están equipadas con tecnología avanzada para monitorización 24/7, control de acceso y sistemas de alarma para garantizar la integridad de tus muestras.",
      cards: [
        { title: "Ensayos de estabilidad ICH", text: "Estudios a largo plazo, intermedios y acelerados conforme a las directrices ICH." },
        { title: "Ensayos de estrés", text: "Evaluación de la estabilidad bajo condiciones exigentes: luz, pH, humedad y oxidación." },
        { title: "Ensayos de estabilidad en uso", text: "Evaluación de la estabilidad durante escenarios de uso típicos." },
        { title: "Ciclos de temperatura", text: "Simulación de ciclos de congelación-descongelación y estrés de transporte." },
        { title: "Desarrollo y validación de métodos indicadores de estabilidad", text: "Seguimiento preciso de la degradación del producto." },
        { title: "Gestión integral del estudio", text: "Desde el desarrollo del protocolo hasta la elaboración del informe, ofrecemos soporte de principio a fin." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Un enfoque estructurado para los ensayos de estabilidad que asegura el cumplimiento y la fiabilidad.",
    processSteps: [
      { title: "1. Diseño del estudio", text: "Definición de protocolos basados en directrices ICH y requisitos del producto." },
      { title: "2. Almacenamiento de muestras", text: "Colocación de muestras en cámaras de estabilidad conformes con ICH con monitorización 24/7." },
      { title: "3. Ensayos", text: "Realización de análisis programados en puntos de tiempo definidos." },
      { title: "4. Informe", text: "Entrega de informes de estabilidad completos para presentación regulatoria." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Con QbD Group obtienes:",
    partnerBullets: [
      "Experiencia profunda — décadas de experiencia en ensayos de estabilidad en diversas industrias.",
      "Instalaciones de última generación — almacenamiento de estabilidad plenamente asegurado y conforme con ICH.",
      "Soluciones a medida — estrategias de ensayo personalizadas alineadas con los requisitos de tu producto.",
      "Soporte regulatorio — conocimiento profundo de los estándares regulatorios globales.",
      "Servicios de principio a fin — desde el diseño del estudio hasta la ejecución, gestionamos cada paso.",
    ],
  },

  /* ───────── Validación de la limpieza ───────── */
  "cleaning-validation": {
    title: "Validación de la limpieza",
    subtitle: "Limpieza eficaz, protegiendo la seguridad del paciente",
    description: "QbD Group te ayuda a definir e implementar las mejores prácticas de validación de limpieza, asegurando el cumplimiento de los estándares de la industria y los requisitos regulatorios.",
    faqItems: [
      { q: "¿Qué es la validación de la limpieza?", a: "Es un proceso documentado que garantiza que los equipos de fabricación están libres de residuos de productos anteriores, agentes de limpieza o contaminantes microbiológicos." },
      { q: "¿Qué métodos analíticos utilizáis?", a: "Empleamos análisis de residuos de API, ensayos de agentes de limpieza en el rango de PPM, análisis de muestras de frotis y validación de métodos analíticos específicos." },
      { q: "¿Ofrecéis soporte in situ?", a: "Sí, proporcionamos asistencia práctica para la recolección de muestras de frotis y la configuración de la validación." },
    ],
    whatTitle: "¿Qué es la validación de la limpieza?",
    whatParagraphs: [
      "La validación de la limpieza es un <strong class='text-foreground'>proceso documentado que asegura que los equipos de fabricación están libres de residuos</strong> de productos anteriores, agentes de limpieza o contaminantes microbiológicos. Confirma que los procedimientos de limpieza resultan consistentemente en niveles aceptables de limpieza.",
      "Los componentes clave incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Análisis de residuos</strong>: detección de residuos de API y agentes de limpieza.</li><li><strong>Ensayos de frotis</strong>: muestreo de superficies para verificar la limpieza.</li><li><strong>Validación de métodos analíticos</strong>: métodos capaces de detectar contaminantes en el rango de PPM.</li><li><strong>Protocolos e informes</strong>: desarrollo de planes integrales de validación de limpieza.</li></ul>",
    ],
    whyTitle: "¿Por qué es esencial la validación de la limpieza?",
    whyBenefits: [
      { title: "Seguridad del paciente", text: "Elimina los riesgos de contaminación cruzada, asegurando productos seguros y eficaces." },
      { title: "Cumplimiento normativo", text: "Cumple con los estrictos estándares de la industria y la normativa." },
      { title: "Integridad del producto", text: "Mantiene la calidad del producto previniendo la contaminación." },
      { title: "Eficiencia operativa", text: "Identifica posibles ineficiencias en los procesos de limpieza." },
      { title: "Acceso a mercados globales", text: "Demuestra el cumplimiento de estándares internacionales." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "En QbD Group, ofrecemos una gama integral de servicios de validación de limpieza:",
      cards: [
        { title: "Análisis de residuos", text: "Detección precisa de residuos de API y agentes de limpieza en el rango de PPM." },
        { title: "Análisis de muestras de frotis", text: "Soporte in situ para muestreo de frotis, cubriendo residuos químicos y microbiológicos." },
        { title: "Validación de métodos analíticos", text: "Desarrollo y validación de métodos para asegurar la fiabilidad y precisión." },
        { title: "Desarrollo de protocolos e informes", text: "Creación y gestión de protocolos de validación de limpieza con informes completos del estudio." },
        { title: "Asistencia in situ", text: "Soporte práctico para la recolección de muestras de frotis y la configuración de la validación." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Un enfoque estructurado y conforme para la validación de la limpieza.",
    processSteps: [
      { title: "1. Evaluación", text: "Evaluación de tus procesos de limpieza y definición del alcance de la validación." },
      { title: "2. Desarrollo del protocolo", text: "Creación de protocolos de validación detallados y criterios de aceptación." },
      { title: "3. Análisis de muestras", text: "Realización de análisis de residuos y frotis en condiciones GMP." },
      { title: "4. Informe", text: "Entrega de informes de validación completos con hallazgos accionables." },
    ],
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "QbD Group es tu socio de confianza para la validación de la limpieza:",
    partnerBullets: [
      "Amplia experiencia — años de experiencia en validación de limpieza en industrias reguladas.",
      "Conocimiento regulatorio — comprensión profunda de los requisitos y estándares regulatorios globales.",
      "Soluciones a medida — estrategias de validación de limpieza personalizadas para tus procesos específicos.",
      "Soporte integral — asistencia de principio a fin, desde la creación del protocolo hasta el informe final.",
      "QbD Group dispone de licencia GMP, certificado ISO 17025 y está auditado por la FAGG belga.",
    ],
  },

  /* ───────── Ensayos de disolventes residuales (GC) ───────── */
  "residual-solvent-testing-gc": {
    title: "Ensayos de disolventes residuales (GC)",
    subtitle: "Análisis fiable y alineado con GMP para APIs y productos terminados",
    description: "Los ensayos de disolventes residuales identifican y cuantifican trazas de disolventes orgánicos tras la fabricación mediante métodos validados de GC-FID y GC-MS, asegurando el cumplimiento con ICH Q3C.",
    faqItems: [
      { q: "¿Qué son los disolventes residuales?", a: "Son disolventes orgánicos volátiles que permanecen en sustancias farmacéuticas, intermedios, excipientes o formas farmacéuticas terminadas. No tienen efecto terapéutico y deben permanecer dentro de límites toxicológicamente aceptables." },
      { q: "¿Qué técnicas analíticas utilizáis?", a: "Utilizamos GC-FID para cuantificación rutinaria y GC-MS para identificación confirmatoria, mediante métodos de headspace validados." },
      { q: "¿Es conforme con ICH Q3C?", a: "Sí, todos nuestros ensayos de disolventes residuales están alineados con las directrices ICH Q3C y los estándares farmacopeicos." },
    ],
    whatTitle: "¿Qué son los ensayos de disolventes residuales?",
    whatParagraphs: [
      "Los ensayos de disolventes residuales miden <strong class='text-foreground'>disolventes orgánicos volátiles en sustancias farmacéuticas, intermedios, excipientes o formas farmacéuticas terminadas</strong>. Estos disolventes no tienen efecto terapéutico y deben permanecer dentro de límites toxicológicamente aceptables.",
      "Los ensayos típicos incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Métodos compendiales y validados</strong> alineados con ICH Q3C y estándares farmacopeicos.</li><li><strong>Análisis headspace GC</strong>: GC-FID para cuantificación rutinaria, GC-MS para identificación confirmatoria.</li><li><strong>Cobertura integral de matrices</strong>, desde APIs hasta productos terminados.</li><li><strong>Informes claros</strong> frente a criterios de aceptación predefinidos.</li></ul>",
    ],
    whyTitle: "¿Por qué son importantes los ensayos de disolventes residuales?",
    whyBenefits: [
      { title: "Seguridad del paciente", text: "Asegura que los residuos de disolventes se mantengan dentro de límites seguros y toxicológicamente aceptables para proteger a los pacientes." },
      { title: "Cumplimiento normativo", text: "Soporta las presentaciones y el cumplimiento continuo de ICH Q3C y estándares farmacopeicos." },
      { title: "Liberación de lotes fiable", text: "Proporciona datos robustos y validados para decisiones de liberación seguras y Quality Assurance." },
      { title: "Eficiencia operativa", text: "Reduce el riesgo de problemas tardíos durante el escalado, la transferencia técnica o los cambios de producción." },
      { title: "Soporte en gestión de cambios", text: "Confirma el cumplimiento continuado tras modificaciones de proceso, método o instalación." },
    ],
    whyIntro: "QbD Group combina experiencia científica con ejecución alineada con GMP y conocimiento regulatorio, ofreciendo ensayos de disolventes residuales fiables, listos para inspección, en APIs y productos terminados con un socio de confianza.",
    helpCards: {
      heading: "Cómo puede ayudarte QbD Group",
      intro: "Nuestros servicios de ensayos de disolventes residuales se adaptan a tus objetivos, ya sea para liberación, estabilidad, investigaciones o soporte de presentación. Nuestras ofertas incluyen:",
      cards: [
        { title: "Ensayos rutinarios de disolventes residuales (GC-FID)", text: "Cuantificación de disolventes residuales específicos mediante flujos de trabajo headspace GC-FID validados, ideal para ensayos de alto volumen basados en especificaciones." },
        { title: "Identificación confirmatoria e investigación (GC-MS)", text: "Confirmación por GC-MS para picos inesperados, disolventes no objetivo o perfiles atípicos, apoyando decisiones informadas científicamente." },
        { title: "Ejecución y verificación de métodos compendiales", text: "Ejecución y verificación de métodos compendiales bajo un marco conforme con GMP para asegurar la idoneidad para tu matriz de producto." },
        { title: "Soporte de ensayos de estabilidad y ciclo de vida", text: "Monitorización de disolventes residuales dentro de programas de estabilidad y actividades del ciclo de vida, incluyendo escalado, optimización de procesos o transferencia de sitio." },
        { title: "Opciones flexibles de informes", text: "Resultados entregados como Certificado de Análisis o informe de conformidad, con formatos ampliados opcionales para presentaciones regulatorias o toxicológicas." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Ejecución alineada con GMP desde la recepción de la muestra hasta la entrega de resultados.",
    processSteps: [
      { title: "1. Consulta", text: "Comprendemos tus requisitos de ensayo y contexto regulatorio." },
      { title: "2. Preparación de la muestra", text: "Procesamiento de muestras mediante métodos headspace GC validados." },
      { title: "3. Análisis y revisión", text: "Ejecución de GC-FID/GC-MS con pasos de revisión documentados." },
      { title: "4. Informe", text: "Entrega de resultados listos para inspección con trazabilidad completa." },
    ],
    partnerTitle: "¿Por qué QbD Group?",
    partnerIntro: "Al colaborar para ensayos de disolventes residuales, nuestros clientes se benefician de:",
    partnerBullets: [
      "Datos alineados con GMP, listos para inspección, con flujos de trabajo controlados y pasos de revisión documentados.",
      "Sistemas de datos validados que soportan la trazabilidad, pistas de auditoría y gestión de registros conforme.",
      "Ensayos GC fiables sin necesidad de inversión interna en equipos o recursos especializados.",
      "Capacidad escalable para satisfacer la demanda máxima de ensayos durante la transferencia técnica, el escalado y el lanzamiento comercial.",
      "Soporte científico para resultados atípicos, incluyendo confirmación por GC-MS y orientación clara sobre los siguientes pasos.",
    ],
  },

  /* ───────── Servicios microbiológicos ───────── */
  "microbiological-analysis": {
    title: "Servicios microbiológicos",
    subtitle: "Ensayos microbiológicos integrales para la seguridad del paciente",
    description: "QbD Group ofrece servicios expertos de ensayos microbiológicos y validación para garantizar la pureza y seguridad de tus materias primas y productos terminados.",
    faqItems: [
      { q: "¿Qué ensayos microbiológicos ofrecéis?", a: "Ofrecemos recuento microbiano (TAMC y TYMC), exclusión de patógenos, ensayo de eficacia de conservantes (PET), ensayos microbiológicos, test de endotoxinas (LAL), análisis de materia particulada, análisis de agua, growth promotion y monitorización medioambiental." },
      { q: "¿Realizáis monitorización medioambiental in situ?", a: "Sí, ofrecemos servicios de monitorización in situ con equipos especializados para muestreo de aire, gases comprimidos y superficies." },
      { q: "¿Podéis validar métodos microbiológicos?", a: "Sí, garantizamos la idoneidad y precisión de los métodos microbiológicos mediante nuestros servicios de validación." },
    ],
    whatTitle: "¿Qué son los servicios microbiológicos?",
    whatParagraphs: [
      "Los servicios microbiológicos abarcan una gama de <strong class='text-foreground'>métodos de ensayo y validación para evaluar y mantener la pureza y seguridad</strong> de los productos farmacéuticos y los entornos de fabricación.",
      "Estos ensayos garantizan el cumplimiento de los estrictos estándares regulatorios y protegen la calidad de tus productos. Los servicios clave incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Recuento microbiano (TAMC y TYMC)</strong>: medición del recuento aeróbico microbiano total y del recuento combinado de levaduras y mohos.</li><li><strong>Exclusión de patógenos</strong>: verificación de la ausencia de microorganismos dañinos especificados.</li><li><strong>Ensayo de eficacia de conservantes (PET)</strong>: evaluación de la efectividad de los conservantes antimicrobianos.</li><li><strong>Ensayos microbiológicos</strong>: cuantificación de la potencia de antibióticos.</li><li><strong>Test de endotoxinas (LAL)</strong>: detección de endotoxinas bacterianas.</li><li><strong>Análisis de materia particulada</strong>: medición de partículas visibles y subvisibles.</li><li><strong>Análisis de agua</strong>: ensayos de agua para uso farmacéutico, incluyendo TOC y conductividad.</li><li><strong>Growth promotion</strong>: verificación del rendimiento de los medios de cultivo.</li><li><strong>Monitorización medioambiental</strong>: evaluación de contaminación en aire, gases comprimidos y superficies.</li><li><strong>Validación de ensayos microbiológicos</strong>: asegurando la exactitud e idoneidad de los métodos.</li></ul>",
    ],
    whyTitle: "¿Por qué son esenciales los servicios microbiológicos?",
    whyBenefits: [
      { title: "Seguridad del paciente", text: "Previene la contaminación y garantiza la seguridad del producto para los usuarios finales." },
      { title: "Cumplimiento normativo", text: "Cumple con los estrictos requisitos de las autoridades regulatorias globales." },
      { title: "Calidad del producto", text: "Mantiene la integridad y eficacia de tus productos." },
      { title: "Confianza operativa", text: "Identifica riesgos potenciales de contaminación para soluciones proactivas." },
      { title: "Control medioambiental", text: "Monitoriza y mantiene los estándares de salas limpias." },
    ],
    helpCards: {
      heading: "¿Cómo puede ayudarte QbD Group?",
      intro: "Nuestro laboratorio dispone de licencia GMP, certificado ISO 17025 y está auditado por la FAGG belga.",
      cards: [
        { title: "Ensayos microbiológicos rutinarios", text: "TAMC, TYMC y exclusión de patógenos para materias primas y productos terminados." },
        { title: "Ensayos especializados", text: "PET, medición de endotoxinas y ensayos de antibióticos." },
        { title: "Monitorización medioambiental", text: "Evaluaciones in situ de aire, gases comprimidos y contaminación superficial." },
        { title: "Servicios de validación", text: "Asegurando la idoneidad y precisión de los métodos microbiológicos." },
        { title: "Análisis de calidad del agua", text: "TOC, conductividad y ensayos de pureza microbiológica para agua de grado farmacéutico." },
        { title: "Soporte in situ", text: "Asistencia práctica para monitorización medioambiental y muestreo." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Un flujo de trabajo riguroso y conforme con GMP para ensayos microbiológicos.",
    processSteps: [
      { title: "1. Consulta", text: "Definición de tu programa de ensayos y necesidades regulatorias." },
      { title: "2. Recolección de muestras", text: "Muestreo in situ o en laboratorio con protocolos de manipulación adecuados." },
      { title: "3. Análisis", text: "Ensayos en condiciones controladas con métodos validados." },
      { title: "4. Informe", text: "Informes completos con resultados accionables y recomendaciones." },
    ],
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Al elegir QbD Group para servicios microbiológicos, te beneficias de:",
    partnerBullets: [
      "Experiencia — décadas de experiencia en ensayos microbiológicos y validación.",
      "Instalaciones avanzadas — laboratorios de última generación equipados con tecnología de vanguardia.",
      "Conocimiento regulatorio — comprensión profunda de los estándares de cumplimiento globales.",
      "Soluciones integrales — desde ensayos rutinarios hasta validaciones complejas.",
      "Enfoques a medida — estrategias personalizadas alineadas con tus requisitos únicos.",
    ],
  },

  /* ───────── Impurezas elementales ───────── */
  "elemental-impurities": {
    title: "Impurezas elementales",
    subtitle: "Garantizando el cumplimiento de las directrices ICH Q3D",
    description: "QbD Group ofrece soporte experto para analizar, controlar y mitigar las impurezas elementales de acuerdo con las directrices ICH Q3D.",
    faqItems: [
      { q: "¿Qué son las impurezas elementales?", a: "Son trazas de metales que pueden introducirse en los productos medicinales durante la fabricación, el envasado o el almacenamiento, y que deben controlarse para cumplir con los requisitos regulatorios y garantizar la seguridad del producto." },
      { q: "¿Qué método analítico utilizáis?", a: "Utilizamos ICP-MS (espectrometría de masas con plasma acoplado inductivamente), que ofrece límites de detección bajos y resultados precisos para la determinación de impurezas elementales." },
      { q: "¿Ofrecéis evaluaciones de riesgo?", a: "Sí, realizamos evaluaciones de riesgo para identificar fuentes potenciales de impurezas elementales y desarrollamos estrategias de control para mitigarlas." },
    ],
    whatTitle: "¿Qué son las impurezas elementales?",
    whatParagraphs: [
      "Las impurezas elementales son <strong class='text-foreground'>trazas de metales que pueden introducirse en los productos medicinales</strong> durante la fabricación, el envasado o el almacenamiento. Deben monitorizarse cuidadosamente para cumplir con los requisitos regulatorios y garantizar la seguridad del producto.",
      "Estas impurezas deben controlarse rigurosamente para cumplir con los estrictos requisitos regulatorios y garantizar la seguridad del producto. Los aspectos clave incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Cumplimiento ICH Q3D</strong>: conforme a las directrices del Consejo Internacional para la Armonización para el control de impurezas elementales.</li><li><strong>Métodos de ensayo</strong>: análisis mediante procedimientos de farmacopea, como ICP-MS (espectrometría de masas con plasma acoplado inductivamente).</li></ul>",
    ],
    whyTitle: "¿Por qué es esencial monitorizarlas?",
    whyBenefits: [
      { title: "Seguridad del paciente", text: "Minimiza los riesgos tóxicos asociados a los metales pesados." },
      { title: "Cumplimiento normativo", text: "Cumple con los requisitos de ICH Q3D y la farmacopea." },
      { title: "Mitigación de riesgos", text: "Identifica fuentes de impurezas elementales durante las evaluaciones de riesgo." },
      { title: "Calidad del producto", text: "Asegura la integridad de las materias primas y los productos terminados." },
      { title: "Operaciones optimizadas", text: "Evita retrasos o rechazos por no conformidad." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "En QbD Group, ofrecemos soporte integral para la gestión de impurezas elementales:",
      cards: [
        { title: "Análisis ICP-MS", text: "Ensayos de última generación para impurezas elementales y metales pesados con bajos límites de detección." },
        { title: "Evaluaciones de riesgo", text: "Evaluación de fuentes potenciales de impurezas elementales en tus procesos y materiales." },
        { title: "Estrategias de control", text: "Implementación de planes eficaces para gestionar y mitigar las impurezas elementales." },
        { title: "Lab Services acreditados", text: "Ensayos fiables y eficientes con visibilidad clara de costes desde el inicio." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Un enfoque conforme para el análisis de impurezas elementales.",
    processSteps: [
      { title: "1. Evaluación de riesgos", text: "Identificación de fuentes potenciales de impurezas elementales en tus productos." },
      { title: "2. Análisis ICP-MS", text: "Ensayos precisos con instrumentación avanzada." },
      { title: "3. Estrategia de control", text: "Desarrollo de planes para gestionar y mitigar los riesgos identificados." },
      { title: "4. Informe", text: "Entrega de resultados detallados con documentación lista para la regulación." },
    ],
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "QbD Group ofrece una experiencia inigualable en la gestión de impurezas elementales:",
    partnerBullets: [
      "Conocimiento experto — comprensión profunda de las directrices ICH Q3D y los estándares farmacopeicos.",
      "Tecnología avanzada — equipo ICP-MS de última generación para resultados precisos y fiables.",
      "Procesos eficientes — tiempos de respuesta rápidos y estructuras de costes transparentes.",
      "Soporte integral — desde la evaluación de riesgos hasta los ensayos rutinarios.",
      "Soluciones proactivas — orientación sobre mejores prácticas para mantener el cumplimiento.",
    ],
  },

  /* ───────── Liberación de lotes QP ───────── */
  "qp-release-and-eu-import": {
    title: "Liberación de lotes QP",
    subtitle: "Ensayos GMP fiables y liberación de lotes en la UE",
    description: "QbD Group ofrece servicios integrales de liberación de lotes por Persona Cualificada (QP), asegurando que tus productos medicinales cumplan con los estrictos estándares regulatorios de la UE.",
    faqItems: [
      { q: "¿Qué es la liberación de lotes QP?", a: "Es un requisito regulatorio de la Unión Europea para certificar que los productos medicinales cumplen con las directrices GMP de la UE. Incluye ensayos rigurosos y revisión de los procesos de fabricación, envasado y transporte." },
      { q: "¿Podéis actuar como MAH?", a: "Sí, podemos actuar como Titular de Autorización de Comercialización (MAH) para empresas que no disponen de una entidad en la UE." },
      { q: "¿Qué productos cubrís?", a: "Cubrimos tanto medicamentos estériles como no estériles, medicamentos en investigación (IMP) y productos comercializados." },
    ],
    whatTitle: "¿Qué es la liberación de lotes QP?",
    whatParagraphs: [
      "La liberación de lotes QP es un <strong class='text-foreground'>requisito regulatorio de la Unión Europea</strong> para certificar que los productos medicinales cumplen con las directrices GMP de la UE. Incluye ensayos rigurosos y revisión de los procesos de fabricación, envasado y transporte.",
      "Los aspectos clave incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Ensayos GMP de lotes</strong>: ensayos para productos medicinales tanto estériles como no estériles.</li><li><strong>Cumplimiento del Anexo 16</strong>: certificación de la liberación de lotes según las directrices GMP de la UE.</li><li><strong>Despacho de importación UE</strong>: gestión de las responsabilidades de importación para fabricantes fuera de la UE.</li><li><strong>Servicios MAH</strong>: actuación como Titular de Autorización de Comercialización para empresas sin entidad en la UE.</li></ul>",
    ],
    whyTitle: "¿Por qué es esencial la liberación de lotes QP?",
    whyBenefits: [
      { title: "Cumplimiento normativo", text: "Cumple con las estrictas directrices de la EMA y GMP de la UE, incluyendo el Anexo 16." },
      { title: "Seguridad del producto", text: "Confirma la calidad y seguridad de los productos medicinales." },
      { title: "Acceso al mercado", text: "Facilita la importación y liberación dentro del Espacio Económico Europeo." },
      { title: "Preparación para ensayos clínicos", text: "Certifica los Medicamentos en Investigación (IMP) para uso clínico." },
      { title: "Garantía operativa", text: "Mitiga los riesgos de no conformidad, asegurando operaciones fluidas." },
    ],
    helpCards: {
      heading: "¿Cómo puede ayudarte QbD Group?",
      intro: "QbD Group ofrece una suite completa de servicios de liberación de lotes QP:",
      cards: [
        { title: "Ensayos GMP de lotes", text: "Transferencia de métodos analíticos (AMT), validación (AMV) y ensayos para lotes clínicos o comerciales." },
        { title: "Declaraciones QP", text: "Emisión de declaraciones para IMP y productos comercializados." },
        { title: "Gestión de muestras de retención", text: "Almacenamiento de muestras de retención y referencia." },
        { title: "Gestión de calidad", text: "Auditoría de la cadena de suministro de fabricación y revisión de la documentación de lotes." },
        { title: "Servicios regulatorios", text: "Actuación como MAH y gestión del despacho de importación UE." },
        { title: "Estudios de estabilidad en curso", text: "Ensayos de estabilidad para garantizar la calidad del producto a largo plazo." },
        { title: "Gestión de desviaciones y CAPA", text: "Resolución de resultados fuera de especificación, controles de cambio y acciones correctivas." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Liberación de lotes QP de principio a fin con experiencia regulatoria.",
    processSteps: [
      { title: "1. Revisión del lote", text: "Revisión de la documentación de fabricación, envasado y transporte." },
      { title: "2. Ensayos GMP", text: "Realización de ensayos analíticos en condiciones GMP." },
      { title: "3. Certificación QP", text: "La Persona Cualificada certifica el cumplimiento del lote con GMP de la UE." },
      { title: "4. Liberación al mercado", text: "Facilitación del despacho de importación UE y la liberación al mercado." },
    ],
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Colaborar con QbD Group para la liberación de lotes QP te ofrece:",
    partnerBullets: [
      "Expertos cualificados — Personas Cualificadas internas con profunda experiencia regulatoria.",
      "Servicios integrados — soluciones integrales de ensayos GMP y liberación de lotes.",
      "Soporte regulatorio — experiencia en la navegación de la Directiva 2001/83/CE de la EMA y el Anexo 16 GMP de la UE.",
      "Soluciones a medida — servicios personalizados para las necesidades específicas de tu producto y mercado.",
      "Acceso al mercado global — soporte para fabricantes fuera de la UE para importar y liberar productos en el EEE.",
    ],
  },

  /* ───────── Monitorización medioambiental ───────── */
  "environmental-monitoring": {
    title: "Monitorización medioambiental",
    subtitle: "Monitorización fiable para el control de la contaminación",
    description: "QbD Group está especializado en monitorización medioambiental para ayudarte a mantener el cumplimiento de salas limpias y garantizar la integridad de tus procesos de fabricación.",
    faqItems: [
      { q: "¿Qué incluye la monitorización medioambiental?", a: "Incluye muestreo activo de aire, muestreo de gases comprimidos, validación de salas limpias y monitorización in situ para la cualificación y validación." },
      { q: "¿Ofrecéis servicios in situ?", a: "Sí, proporcionamos servicios de monitorización in situ en tiempo real con equipos especializados para muestreo y análisis." },
      { q: "¿En qué normativas os basáis?", a: "Nos basamos en los estándares de salas limpias y las directrices regulatorias aplicables, incluyendo las GMP de la UE y la ISO 14644." },
    ],
    whatTitle: "¿Qué es la monitorización medioambiental?",
    whatParagraphs: [
      "La monitorización medioambiental es un <strong class='text-foreground'>proceso crítico para evaluar y mantener la limpieza de entornos controlados</strong>. Implica ensayos rutinarios de aire, gases, superficies y otros parámetros críticos para detectar contaminantes.",
      "Los aspectos clave incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Muestreo activo de aire</strong>: detección de partículas viables y no viables.</li><li><strong>Muestreo de gases comprimidos</strong>: evaluación de la pureza microbiológica y el recuento de partículas.</li><li><strong>Validación de salas limpias</strong>: asegurando que las salas limpias cumplen con los requisitos regulatorios.</li><li><strong>Monitorización in situ</strong>: muestreo y análisis en tiempo real para la cualificación y validación.</li></ul>",
    ],
    whyTitle: "¿Por qué es esencial la monitorización medioambiental?",
    whyBenefits: [
      { title: "Seguridad del producto", text: "Protege los productos de la contaminación para garantizar la seguridad del paciente." },
      { title: "Cumplimiento normativo", text: "Cumple con los estándares de salas limpias y las directrices regulatorias." },
      { title: "Integridad del proceso", text: "Mantiene entornos de fabricación limpios para prevenir errores costosos." },
      { title: "Continuidad operativa", text: "Detecta y aborda los riesgos de contaminación antes de que se agraven." },
      { title: "Quality Assurance", text: "Proporciona datos robustos para respaldar tus esfuerzos de control de calidad." },
    ],
    helpCards: {
      heading: "¿Cómo puede ayudarte QbD Group?",
      intro: "QbD Group ofrece servicios integrales de monitorización medioambiental adaptados a tus necesidades:",
      cards: [
        { title: "Equipos de última generación", text: "Muestreadores activos de aire, contadores de partículas y analizadores TOC." },
        { title: "Muestreo de gases comprimidos", text: "Equipos especializados para análisis microbiológico y de partículas de gases." },
        { title: "Servicios de monitorización in situ", text: "Muestreo y ensayos en tiempo real para la cualificación y validación de salas limpias." },
        { title: "Soporte de validación", text: "Asistencia en la validación y el mantenimiento del cumplimiento de salas limpias." },
        { title: "Implementación de mejores prácticas", text: "Desarrollo de protocolos y prácticas para una monitorización eficaz." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Un enfoque sistemático para el control de la contaminación y el cumplimiento de salas limpias.",
    processSteps: [
      { title: "1. Evaluación del sitio", text: "Evaluación de tu entorno de sala limpia y necesidades de monitorización." },
      { title: "2. Muestreo", text: "Muestreo in situ de aire, gases y superficies con equipos especializados." },
      { title: "3. Análisis", text: "Procesamiento de muestras y evaluación de resultados frente a estándares regulatorios." },
      { title: "4. Informe y recomendaciones", text: "Informes detallados con orientación accionable para mantener el cumplimiento." },
    ],
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Al elegir QbD Group para la monitorización medioambiental, obtienes:",
    partnerBullets: [
      "Ingenieros expertos — profesionales QC cualificados con experiencia en control de la contaminación.",
      "Tecnología avanzada — equipos de última generación para ensayos precisos y fiables.",
      "Soporte integral — servicios de principio a fin, desde la recolección de muestras hasta la validación de salas limpias.",
      "Experiencia regulatoria — comprensión profunda de los estándares de salas limpias y el cumplimiento.",
      "Soluciones a medida — estrategias adaptadas a tus necesidades específicas de sala limpia y proceso.",
    ],
  },

  /* ───────── Proyectos I+D ───────── */
  "rd-projects": {
    title: "Proyectos I+D",
    subtitle: "Soluciones personalizadas para la investigación y el desarrollo farmacéutico",
    description: "QbD Group aprovecha su profunda experiencia científica y las tecnologías más avanzadas para apoyar proyectos I+D farmacéuticos con soluciones a medida.",
    faqItems: [
      { q: "¿Qué tipo de proyectos I+D apoyáis?", a: "Apoyamos proyectos que requieren soluciones personalizadas, resolución de problemas para métodos QC, retos multidisciplinares y desarrollo de nuevos productos o procesos." },
      { q: "¿Podéis invertir en equipos específicos para mi proyecto?", a: "Sí, estamos dispuestos a coinvertir en equipos o configuraciones específicas adaptadas a las necesidades de tu proyecto." },
      { q: "¿Colaboráis con instituciones académicas?", a: "Sí, mantenemos fuertes alianzas con expertos y el mundo académico en toda la industria farmacéutica." },
    ],
    whatTitle: "¿Qué son los proyectos I+D?",
    whatParagraphs: [
      "¿Necesitas una solución personalizada específica para uno de tus proyectos I+D? ¿O necesitas ayuda para resolver problemas en uno de tus métodos QC? Nuestros científicos QC experimentados están a tu servicio, incluso para retos complejos o multidisciplinares.",
      "Los proyectos I+D en la industria farmacéutica se centran en <strong class='text-foreground'>la innovación, la resolución de problemas y el desarrollo de nuevos productos o procesos</strong>. Estos proyectos suelen requerir una combinación de experiencia técnica avanzada, equipos de última generación y estrecha colaboración entre las partes interesadas.",
      "Los aspectos clave incluyen:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>Resolución de problemas</strong>: aplicación de conocimientos científicos y enfoques creativos.</li><li><strong>Soluciones personalizadas</strong>: desarrollo de estrategias a medida para cumplir los objetivos específicos del proyecto.</li><li><strong>Tecnología de vanguardia</strong>: utilización de equipos avanzados para resultados precisos.</li><li><strong>Desarrollo colaborativo</strong>: trabajo estrecho con socios de la industria y el ámbito académico.</li></ul>",
    ],
    whyTitle: "¿Por qué son esenciales los proyectos I+D?",
    whyBenefits: [
      { title: "Fomentar la innovación", text: "Impulsan la creación de nuevos productos, métodos y soluciones." },
      { title: "Acelerar el desarrollo", text: "Optimizan procesos para reducir el time-to-market." },
      { title: "Mejorar la calidad del producto", text: "Abordan retos para garantizar un rendimiento óptimo." },
      { title: "Preparación regulatoria", text: "Soportan el cumplimiento con los estándares de la industria." },
      { title: "Fortalecer la competitividad", text: "Aprovechan la innovación para mantener una posición de mercado líder." },
    ],
    helpCards: {
      heading: "¿Cómo podemos ayudarte?",
      intro: "QbD Group ofrece una amplia gama de soporte para proyectos I+D farmacéuticos:",
      cards: [
        { title: "Experiencia científica", text: "Amplio conocimiento y resolución creativa de problemas para diversas técnicas QC." },
        { title: "Cooperación flexible", text: "Procesos de desarrollo rápidos y ágiles con gestión de proyecto dedicada." },
        { title: "Equipamiento avanzado", text: "Acceso a herramientas de última generación para investigación especializada." },
        { title: "Oportunidades de coinversión", text: "Disposición a invertir en equipos o configuraciones específicas adaptadas a tus necesidades." },
        { title: "Redes colaborativas", text: "Sólidas alianzas con expertos y el mundo académico en toda la industria farmacéutica." },
      ],
    },
    processTitle: "Nuestro proceso",
    processIntro: "Ejecución I+D colaborativa y ágil adaptada a tu proyecto.",
    processSteps: [
      { title: "1. Descubrimiento", text: "Comprendemos tu reto y definimos los objetivos del proyecto." },
      { title: "2. Desarrollo de la estrategia", text: "Diseñamos un enfoque a medida con hitos claros." },
      { title: "3. Ejecución", text: "Realizamos los experimentos con equipos avanzados y supervisión experta." },
      { title: "4. Entrega", text: "Resultados completos con insights accionables para el desarrollo de tu producto." },
    ],
    partnerTitle: "¿Por qué colaborar con QbD Group?",
    partnerIntro: "Al colaborar con QbD Group para proyectos I+D, obtienes:",
    partnerBullets: [
      "Experiencia profunda — conocimiento científico integral en diversas disciplinas.",
      "Soluciones a medida — enfoques personalizados para satisfacer las demandas únicas de tu proyecto.",
      "Enfoque innovador — compromiso con la creatividad y la resolución de problemas fuera de lo convencional.",
      "Capacidades avanzadas — equipos y métodos de última generación para resultados precisos.",
      "Alianzas colaborativas — acceso a una amplia red de expertos de la industria y el ámbito académico.",
    ],
  },
};

/* ───────────────────────────────────────────────────
   Simplified Chinese (zh-cn) translations.
   ─────────────────────────────────────────────────── */
const labOfferingsZh: Record<string, LabOfferingI18n> = {
  "chemical-analysis": {
    title: "QC 化学分析",
    subtitle: "为您的制药需求提供全面的分析检测",
    description: "QbD Group 经 GMP 认证的 QC 实验室,为原料、API、IMP 以及成品药提供可靠、精准的化学分析服务。",
    faqItems: [
      { q: "你们提供哪些类型的化学分析?", a: "我们提供鉴别试验(IR、TLC、HPLC)、含量测定(UV/Vis、自动滴定、HPLC/UHPLC、GC)、剂量均一性、杂质检测、物理性质测定,以及 DSC 和激光粒度分布等专项检测。" },
      { q: "你们的实验室是否经过 GMP 认证?", a: "是的,我们的实验室持有 GMP 许可证,并获得 ISO 17025 认证,接受比利时 FAGG 的审计。" },
      { q: "你们能分析高活性产品吗?", a: "可以,我们的实验室获授权处理精神药物、麻醉药品、激素和高活性产品。" },
      { q: "通常的检测周期是多久?", a: "我们致力于提供高效的周转时间,具体周期视检测的复杂程度而定。具体时间会在初次咨询时确认。" },
    ],
    whatTitle: "什么是 QC 化学分析?",
    whatParagraphs: [
      "质量控制(QC)化学分析包含一系列<strong class='text-foreground'>分析检测和程序,用于验证药品的质量、安全性和有效性</strong>。从原料到成品剂型,QC 化学分析确保符合严格的法规标准,并帮助维持产品完整性。",
      "关键方面包括:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>药典检测</strong>:验证是否符合药典标准。</li><li><strong>成品检测</strong>:依据法规档案进行符合性检测。</li><li><strong>先进仪器设备</strong>:采用尖端技术提供精确结果。</li><li><strong>法规合规</strong>:确保方法符合 GMP 等要求。</li></ul>",
    ],
    whyTitle: "为什么 QC 化学分析至关重要?",
    whyBenefits: [
      { title: "产品安全", text: "确保药品不含有害杂质。" },
      { title: "法规合规", text: "满足 GMP 标准和药典要求。" },
      { title: "质量保证", text: "确认原料与成品的质量与一致性。" },
      { title: "运营效率", text: "通过及早发现问题,降低召回与不合规风险。" },
      { title: "工艺验证", text: "以可靠数据支持稳健的生产工艺。" },
    ],
    helpCards: {
      heading: "我们如何为您提供帮助?",
      intro: "我们的 QC 实验室提供广泛的化学分析服务,包括:",
      cards: [
        { title: "鉴别试验", text: "IR、TLC 以及配备多种检测器的 HPLC。" },
        { title: "外观检测", text: "评估颜色与澄清度。" },
        { title: "含量测定", text: "采用 UV/Vis、自动滴定、HPLC/UHPLC 以及 GC 技术。" },
        { title: "剂量均一性检测", text: "确保药品中各剂量的一致性。" },
        { title: "杂质检测", text: "定量分析有关物质、残留溶剂和元素杂质。" },
        { title: "物理性质测定", text: "测量 pH、粘度、密度等。" },
        { title: "固体口服剂型检测", text: "包括崩解、溶出、硬度和脆碎度。" },
        { title: "专项检测", text: "DSC、TOC 分析、激光粒度分布以及微粒物质评估。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "我们采用结构化方法,提供可靠且符合 GMP 的分析结果。",
    processSteps: [
      { title: "1. 咨询", text: "了解您的分析需求和产品要求。" },
      { title: "2. 方法选择", text: "为您的样品选择最佳分析方案。" },
      { title: "3. 分析", text: "在 GMP 条件下使用先进仪器执行检测。" },
      { title: "4. 报告", text: "交付符合法规要求的完整文件与结果。" },
    ],
    partnerTitle: "为什么选择 QbD Group 作为合作伙伴?",
    partnerIntro: "选择 QbD Group 进行 QC 化学分析,您将获得:",
    partnerBullets: [
      "全面的专业能力 — 从一般性鉴别到先进的杂质检测,覆盖广泛的分析需求。",
      "GMP 认证 — 获得许可可处理精神药物、麻醉药品、激素和高活性产品。",
      "先进的设备 — 采用尖端技术,实现精准、高效的检测。",
      "灵活的支持 — 全方位分析服务,助您专注核心业务。",
      "法规保障 — 服务设计满足全球合规标准。",
    ],
  },
  "cleaning-validation": {
    title: "清洁验证",
    subtitle: "有效的清洁，保障患者安全",
    description: "QbD Group 帮助您定义并实施清洁验证的最佳实践，确保符合行业标准与法规要求。",
    faqItems: [
      { q: "什么是清洁验证？", a: "这是一项有文档记录的流程，用于确保生产设备不残留先前产品、清洁剂或微生物污染物。" },
      { q: "你们使用哪些分析方法？", a: "我们采用 API 残留分析、PPM 级别的清洁剂残留检测、擦拭样品分析以及特定分析方法验证。" },
      { q: "你们是否提供现场支持？", a: "是的，我们为擦拭样品采集与验证设置提供动手协助。" },
    ],
    whatTitle: "什么是清洁验证？",
    whatParagraphs: [
      "清洁验证是一项<strong class='text-foreground'>有文档记录的流程，用于确保生产设备不残留</strong>先前产品、清洁剂或微生物污染物。它确认清洁程序能够持续达到可接受的清洁度水平。",
      "关键组成部分包括：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>残留物分析</strong>：检测 API 与清洁剂残留。</li><li><strong>擦拭测试</strong>：对表面取样以验证清洁度。</li><li><strong>分析方法验证</strong>：确保方法可在 PPM 级别检测污染物。</li><li><strong>方案与报告</strong>：制定全面的清洁验证计划。</li></ul>",
    ],
    whyTitle: "为什么清洁验证至关重要？",
    whyBenefits: [
      { title: "患者安全", text: "消除交叉污染风险，确保产品安全有效。" },
      { title: "法规合规", text: "满足严格的行业与法规标准。" },
      { title: "产品完整性", text: "通过防止污染保持产品质量。" },
      { title: "运营效率", text: "识别清洁流程中潜在的低效环节。" },
      { title: "全球市场准入", text: "证明符合国际标准。" },
    ],
    helpCards: {
      heading: "我们如何提供帮助？",
      intro: "在 QbD Group，我们提供全面的清洁验证服务：",
      cards: [
        { title: "残留物分析", text: "在 PPM 级别精准检测 API 残留与清洁剂。" },
        { title: "擦拭样品分析", text: "现场支持擦拭取样，涵盖化学与微生物残留。" },
        { title: "分析方法验证", text: "开发并验证方法，以确保可靠性与准确性。" },
        { title: "方案制定与报告", text: "编制并管理清洁验证方案，并提供完整的研究报告。" },
        { title: "现场协助", text: "为擦拭样品采集与验证设置提供动手支持。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "一种结构化、合规的清洁验证方法。",
    processSteps: [
      { title: "1. 评估", text: "评估您的清洁流程并界定验证范围。" },
      { title: "2. 方案制定", text: "制定详细的验证方案与可接受标准。" },
      { title: "3. 样品分析", text: "在 GMP 条件下进行残留与擦拭分析。" },
      { title: "4. 报告", text: "交付包含可执行结论的全面验证报告。" },
    ],
    partnerTitle: "为什么选择 QbD Group 作为合作伙伴？",
    partnerIntro: "以下是 QbD Group 成为您清洁验证可信赖合作伙伴的原因：",
    partnerBullets: [
      "丰富的专业能力 — 在受监管行业的清洁验证领域拥有多年经验。",
      "法规知识 — 深入了解全球法规要求与标准。",
      "量身定制的解决方案 — 针对您的具体流程提供定制化清洁验证策略。",
      "全方位支持 — 从方案编制到最终报告的端到端协助。",
      "QbD Group 拥有 GMP 许可证、ISO 17025 认证，并接受比利时 FAGG 的审计。",
    ],
  },
  "elemental-impurities": {
    title: "元素杂质",
    subtitle: "确保符合 ICH Q3D 指南",
    description: "QbD Group 提供专业支持，按照 ICH Q3D 指南分析、控制并降低元素杂质风险。",
    faqItems: [
      { q: "什么是元素杂质？", a: "元素杂质是指可能在生产、包装或储存过程中引入药品的痕量金属，必须加以控制，以满足法规要求并保障产品安全。" },
      { q: "你们使用什么分析方法？", a: "我们使用 ICP-MS（电感耦合等离子体质谱法），具备较低的检测限和精确的元素杂质测定结果。" },
      { q: "你们是否提供风险评估？", a: "是的，我们进行风险评估以识别元素杂质的潜在来源，并制定相应的控制策略加以缓解。" },
    ],
    whatTitle: "什么是元素杂质？",
    whatParagraphs: [
      "元素杂质是指可能在生产、包装或储存过程中<strong class='text-foreground'>引入药品的痕量金属</strong>。必须对其进行严密监控，以满足法规要求并确保产品安全。",
      "这些杂质必须严格监控与管理，以满足严格的法规要求并保障产品安全。关键方面包括：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>ICH Q3D 合规</strong>：遵循 International Council for Harmonisation (ICH) 关于元素杂质控制的指南。</li><li><strong>测试方法</strong>：采用药典程序进行分析，例如 ICP-MS（电感耦合等离子体质谱法）。</li></ul>",
    ],
    whyTitle: "为什么监测元素杂质至关重要？",
    whyBenefits: [
      { title: "患者安全", text: "降低重金属带来的毒性风险。" },
      { title: "法规合规", text: "满足 ICH Q3D 与药典要求。" },
      { title: "风险缓解", text: "在风险评估中识别元素杂质的来源。" },
      { title: "产品质量", text: "确保原料与成品的完整性。" },
      { title: "运营顺畅", text: "避免因不合规导致的延误或退回。" },
    ],
    helpCards: {
      heading: "我们如何提供帮助？",
      intro: "在 QbD Group，我们为元素杂质管理提供全面支持：",
      cards: [
        { title: "ICP-MS 分析", text: "采用先进设备进行元素杂质与重金属检测，具备低检测限。" },
        { title: "风险评估", text: "评估您的工艺与物料中元素杂质的潜在来源。" },
        { title: "控制策略", text: "实施有效方案，对元素杂质进行管理与缓解。" },
        { title: "认可实验室服务", text: "提供可靠高效的检测，并提供清晰的前期成本可视化。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "一种合规的元素杂质分析方法。",
    processSteps: [
      { title: "1. 风险评估", text: "识别您产品中元素杂质的潜在来源。" },
      { title: "2. ICP-MS 分析", text: "使用先进的仪器进行精确检测。" },
      { title: "3. 控制策略", text: "制定方案以管理并缓解已识别的风险。" },
      { title: "4. 报告", text: "提供详细结果与符合法规要求的文件。" },
    ],
    partnerTitle: "为什么选择 QbD Group 作为合作伙伴？",
    partnerIntro: "QbD Group 在元素杂质管理方面拥有无与伦比的专业能力：",
    partnerBullets: [
      "专家知识 — 深入掌握 ICH Q3D 指南与药典标准。",
      "先进技术 — 采用尖端的 ICP-MS 设备，提供精确可靠的结果。",
      "高效流程 — 快速的周转时间与透明的成本结构。",
      "全方位支持 — 涵盖从风险评估到常规检测。",
      "前瞻性方案 — 提供最佳实践指导，持续保持合规。",
    ],
  },

  /* ───────── 方法开发 ───────── */
  "method-development": {
    title: "方法开发",
    subtitle: "为精准分析程序量身定制解决方案",
    description: "QbD Group 将深厚的科学专业知识与创新方法相结合，为您量身定制稳健的分析方法。",
    faqItems: [
      { q: "方法开发包括哪些内容？", a: "包括针对您的产品设计、优化和记录分析程序，确保其适用于预期目的并为验证做好准备。" },
      { q: "你们能为复杂产品开发方法吗？", a: "可以，我们的科学家在处理复杂基质、新型配方以及行业特定分析挑战方面拥有丰富经验。" },
      { q: "开发一个方法需要多长时间？", a: "周期取决于复杂程度。简单的方法可能在几周内完成，而更复杂的项目可能需要更长时间。我们会在咨询期间提供明确的预估。" },
    ],
    whatTitle: "什么是方法开发？",
    whatParagraphs: [
      "方法开发涉及<strong class='text-foreground'>设计和优化分析程序</strong>，以准确评估药品的质量、安全性和有效性。该过程确保方法适用于其预期目的，并能够提供可重现的结果。",
      "关键方面包括：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>定制化</strong>：针对特定挑战量身定制方法。</li><li><strong>优化</strong>：优化参数以提高性能和效率。</li><li><strong>适用性设计</strong>：确保方法满足应用需求。</li><li><strong>验证前准备</strong>：建立稳健的方法，为验证做好准备。</li></ul>",
    ],
    whyTitle: "为什么方法开发至关重要？",
    whyBenefits: [
      { title: "质量保证", text: "确保关键分析获得可靠且准确的结果。" },
      { title: "法规合规", text: "使方法符合全球标准和指南。" },
      { title: "效率", text: "通过优化的分析程序减少时间和成本。" },
      { title: "产品完整性", text: "在每个阶段保护产品质量和安全性。" },
      { title: "数据可信度", text: "为验证和常规检测提供坚实基础。" },
    ],
    helpCards: {
      heading: "我们如何为您提供帮助？",
      intro: "在 QbD Group，我们为方法开发提供全面支持：",
      cards: [
        { title: "量身定制方案", text: "根据您的产品和工艺需求设计特定方法。" },
        { title: "尖端技术", text: "利用先进仪器获得精确结果。" },
        { title: "问题解决专业能力", text: "以创新有效的解决方案应对挑战。" },
        { title: "验证前指导", text: "为方法的顺利验证和实施做好准备。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "我们采用系统化的方法来开发稳健的分析方法。",
    processSteps: [
      { title: "1. 需求分析", text: "了解您的产品、工艺和法规背景。" },
      { title: "2. 方法设计", text: "制定针对您需求的优化分析策略。" },
      { title: "3. 优化", text: "优化方法参数，以实现最佳性能和可靠性。" },
      { title: "4. 文件记录与交付", text: "完整文件已准备好进行验证和实施。" },
    ],
    partnerTitle: "为什么选择 QbD Group 作为合作伙伴？",
    partnerIntro: "选择 QbD Group 进行方法开发，您将获得：",
    partnerBullets: [
      "科学卓越 — 在分析化学和制药科学领域拥有深厚的专业知识。",
      "定制解决方案 — 根据您项目的目标量身定制独特方案。",
      "创新工具 — 使用尖端技术和方法。",
      "协作伙伴关系 — 紧密合作以满足您的特定需求。",
    ],
  },


  /* ───────── 方法验证 ───────── */
  "method-validation": {
    title: "方法验证",
    subtitle: "确保分析方法可靠且合规",
    description: "QbD Group 确保您的分析方法符合 ICH Q2(R1) 及药典指南等全球标准,提供准确且可重复的结果。",
    faqItems: [
      { q: "你们遵循哪些方法验证指南?", a: "我们遵循 ICH Q2(R1) 指南、药典标准以及客户特定方案,以确保法规合规。" },
      { q: "你们评估哪些参数?", a: "我们评估准确度、精密度、专属性、线性、范围、检测限、定量限和耐用性。" },
      { q: "你们是否提供可供法规申报的报告?", a: "是的,我们的验证报告专为满足监管机构申报要求而设计。" },
    ],
    whatTitle: "什么是方法验证?",
    whatParagraphs: [
      "方法验证是对分析方法进行<strong class='text-foreground'>系统性评估的过程</strong>,以确保其能够持续提供可靠且准确的结果。该过程验证方法是否符合预定标准,并适用于法规申报和常规使用。",
      "关键方面包括:<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>法规对齐</strong>:遵循 ICH Q2(R1)、药典及客户特定指南。</li><li><strong>参数评估</strong>:准确度、精密度、专属性、线性和耐用性。</li><li><strong>文件记录</strong>:完整的验证研究报告。</li><li><strong>可转移性</strong>:方法已准备就绪,可顺利转移。</li></ul>",
    ],
    whyTitle: "为什么方法验证至关重要?",
    whyBenefits: [
      { title: "法规审批", text: "确保符合国际标准,促进市场准入。" },
      { title: "产品完整性", text: "确认药品的质量、安全性和有效性。" },
      { title: "运营效率", text: "降低不合规风险,提升分析可靠性。" },
      { title: "风险缓解", text: "识别并解决分析方法中潜在的薄弱环节。" },
      { title: "为常规检测做好准备", text: "使方法能够在质量控制中持续应用。" },
    ],
    helpCards: {
      heading: "我们如何为您提供帮助?",
      intro: "在 QbD Group,我们提供全面的方法验证服务:",
      cards: [
        { title: "法规专业知识", text: "遵循 ICH Q2(R1) 及相关指南。" },
        { title: "全面验证", text: "评估所有关键参数,包括准确度、精密度和耐用性。" },
        { title: "定制方案", text: "根据您的需求开发和执行专门的验证方案。" },
        { title: "验证报告", text: "提供法规申报所需的详细文件。" },
        { title: "转移准备", text: "已验证的方法可随时转移至外部实验室。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "我们确保验证工作流程全面且合规。",
    processSteps: [
      { title: "1. 方案制定", text: "制定符合法规标准的验证方案。" },
      { title: "2. 执行", text: "以严谨的文件记录执行验证实验。" },
      { title: "3. 数据分析", text: "对照预定可接受标准评估结果。" },
      { title: "4. 报告与转移", text: "交付可供法规申报的完整报告。" },
    ],
    partnerTitle: "为什么选择 QbD Group 作为合作伙伴?",
    partnerIntro: "选择 QbD Group 进行方法验证,您将获得:",
    partnerBullets: [
      "法规知识 — 在全球法规要求方面拥有丰富经验。",
      "准确的结果 — 严格的验证流程,确保方法的可靠性。",
      "量身定制方案 — 根据您项目的独特需求定制协议。",
      "端到端支持 — 从验证规划到报告编制的全程协助。",
    ],
  },

  "microbiological-analysis": {
    title: "微生物检测服务",
    subtitle: "全面的微生物检测，保障患者安全",
    description: "QbD Group 提供专业的微生物检测与验证服务，确保您的原料和成品的纯度与安全性。",
    faqItems: [],
    whatTitle: "什么是微生物检测服务？",
    whatParagraphs: [
      "微生物检测服务涵盖一系列<strong class='text-foreground'>检测与验证方法，用于评估并维持药品及生产环境的纯度与安全性</strong>。",
      "这些检测确保符合严格的法规标准，并保障您的产品质量。关键服务包括：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>微生物计数 (TAMC & TYMC)</strong>：测定需氧菌总数以及酵母菌和霉菌总数。</li><li><strong>致病菌排除</strong>：确保不存在特定有害微生物。</li><li><strong>防腐剂有效性测试 (PET)</strong>：评估抗菌防腐剂的有效性。</li><li><strong>微生物效价测定</strong>：定量分析抗生素效价。</li><li><strong>内毒素检测 (LAL Test)</strong>：检测细菌内毒素。</li><li><strong>微粒物质分析</strong>：测定可见及亚可见微粒。</li><li><strong>水质分析</strong>：检测制药用水，包括 TOC（总有机碳）和电导率。</li><li><strong>培养基促生长测试</strong>：验证培养基的性能。</li><li><strong>环境监测</strong>：评估空气、压缩气体和表面的污染情况。</li><li><strong>微生物检测方法验证</strong>：确保微生物方法的准确性与适用性。</li></ul>",
    ],
    whyTitle: "为什么微生物检测服务至关重要？",
    whyBenefits: [
      { title: "患者安全", text: "防止污染，确保终端用户的产品安全。" },
      { title: "法规合规", text: "满足全球法规机构的严格要求。" },
      { title: "产品质量", text: "维持产品的完整性与有效性。" },
      { title: "运营信心", text: "识别潜在污染风险，提供前瞻性解决方案。" },
      { title: "环境控制", text: "监测并维持洁净室标准。" },
    ],
    helpCards: {
      heading: "QbD Group 如何为您提供帮助？",
      intro: "我们的实验室持有 GMP 许可证、ISO 17025 认证，并接受比利时 FAGG 的审计。",
      cards: [
        { title: "常规微生物检测", text: "对原料和成品进行 TAMC、TYMC 及致病菌排除检测。" },
        { title: "专项检测", text: "PET、内毒素测定及抗生素效价分析。" },
        { title: "环境监测", text: "现场评估空气、压缩气体和表面污染情况。" },
        { title: "验证服务", text: "确保微生物方法的适用性与准确性。" },
        { title: "水质分析", text: "对制药用水进行 TOC、电导率及微生物纯度检测。" },
        { title: "现场支持", text: "为环境监测和采样提供实地协助。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "严格的 GMP 合规微生物检测工作流程。",
    processSteps: [
      { title: "1. 咨询", text: "明确您的检测方案与法规需求。" },
      { title: "2. 样品采集", text: "按照规范操作程序进行现场或实验室采样。" },
      { title: "3. 分析", text: "在受控条件下使用经验证的方法进行检测。" },
      { title: "4. 报告", text: "提供包含可执行结果与建议的全面报告。" },
    ],
    partnerTitle: "为什么选择 QbD Group 作为合作伙伴？",
    partnerIntro: "选择 QbD Group 进行微生物检测服务，您将受益于：",
    partnerBullets: [
      "专业能力 — 在微生物检测与验证领域拥有数十年的经验。",
      "先进设施 — 配备尖端技术的现代化实验室。",
      "法规知识 — 深入理解全球合规标准。",
      "综合解决方案 — 从常规检测到复杂的验证项目。",
      "量身定制 — 根据您的独特需求制定定制策略。",
    ],
  },
  "environmental-monitoring": {
    title: "环境监测",
    subtitle: "可靠的监测，控制污染",
    description: "QbD Group 专注于环境监测，帮助您维持洁净室合规性，确保生产过程的完整性。",
    faqItems: [
      { q: "环境监测包括哪些内容？", a: "包括主动空气采样、压缩气体采样、洁净室验证以及为确认与验证提供的现场监测。" },
      { q: "你们提供现场服务吗？", a: "是的，我们提供实时现场监测服务，配备专业采样与分析设备。" },
      { q: "你们遵循哪些法规？", a: "我们遵循洁净室标准及适用的法规指南，包括 EU GMP 和 ISO 14644。" },
    ],
    whatTitle: "什么是环境监测？",
    whatParagraphs: [
      "环境监测是<strong class='text-foreground'>评估和维持受控环境洁净度的关键流程</strong>。它涉及对空气、气体、表面及其他关键参数进行常规检测，以发现污染物。",
      "关键方面包括：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>主动空气采样</strong>：检测活性与非活性粒子。</li><li><strong>压缩气体采样</strong>：评估微生物纯度与粒子计数。</li><li><strong>洁净室验证</strong>：确保洁净室符合法规要求。</li><li><strong>现场监测</strong>：为确认与验证提供实时采样与分析。</li></ul>",
    ],
    whyTitle: "为什么环境监测至关重要？",
    whyBenefits: [
      { title: "产品安全", text: "保护产品免受污染，确保患者安全。" },
      { title: "法规合规", text: "满足洁净室标准与法规指南。" },
      { title: "工艺完整性", text: "维持洁净的生产环境，防止代价高昂的错误。" },
      { title: "运营连续性", text: "在污染风险升级前及时发现并处理。" },
      { title: "质量保证", text: "提供可靠数据，支持您的质量控制工作。" },
    ],
    helpCards: {
      heading: "QbD Group 如何为您提供帮助？",
      intro: "QbD Group 提供全面的环境监测服务，根据您的需求量身定制：",
      cards: [
        { title: "先进设备", text: "主动空气采样器、粒子计数器和 TOC 分析仪。" },
        { title: "压缩气体采样", text: "用于气体微生物与粒子分析的专业设备。" },
        { title: "现场监测服务", text: "为洁净室确认与验证提供实时采样与检测。" },
        { title: "验证支持", text: "协助验证并维持洁净室合规性。" },
        { title: "最佳实践实施", text: "制定高效监测的规程与实践。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "系统化控制污染与洁净室合规的方法。",
    processSteps: [
      { title: "1. 现场评估", text: "评估您的洁净室环境与监测需求。" },
      { title: "2. 采样", text: "使用专业设备进行现场空气、气体和表面采样。" },
      { title: "3. 分析", text: "处理样品，并根据法规标准评估结果。" },
      { title: "4. 报告与建议", text: "提供详细报告，并给出维持合规的可执行建议。" },
    ],
    partnerTitle: "为什么选择 QbD Group 作为合作伙伴？",
    partnerIntro: "选择 QbD Group 进行环境监测，您将获得：",
    partnerBullets: [
      "资深工程师 — 拥有污染控制经验的专业 QC 人员。",
      "先进技术 — 采用尖端设备，确保测试准确可靠。",
      "全方位支持 — 从样品采集到洁净室验证的端到端服务。",
      "法规专业知识 — 深入理解洁净室标准与合规要求。",
      "定制解决方案 — 根据您的特定洁净室与工艺需求量身定制策略。",
    ],
  },
  "qp-release-and-eu-import": {
    title: "QP 批次放行",
    subtitle: "可靠的 GMP 批次检测与 EU 批次放行",
    description: "QbD Group 提供全面的 QP 批次放行服务，确保您的药品符合严格的 EU 法规标准。",
    faqItems: [
      { q: "什么是 QP 批次放行？", a: "QP 批次放行是欧盟内的一项法规要求，用于证明药品符合 EU GMP 指南。它涉及对生产、包装和运输流程的严格检测与审查。" },
      { q: "你们可以担任 MAH 吗？", a: "可以，我们可以为在欧盟没有实体的公司担任上市许可持有人 (MAH)。" },
      { q: "你们涵盖哪些产品？", a: "我们涵盖无菌和非无菌药品、临床试验用药品 (IMP) 以及上市产品。" },
    ],
    whatTitle: "什么是 QP 批次放行？",
    whatParagraphs: [
      "QP 批次放行是<strong class='text-foreground'>欧盟内的一项法规要求</strong>，用于证明药品符合 EU 良好生产规范 (GMP) 指南。它涉及对生产、包装和运输流程的严格检测与审查。",
      "关键方面包括：<ul class='list-disc pl-6 mt-3 space-y-2'><li><strong>GMP 批次检测</strong>：针对无菌和非无菌药品的检测。</li><li><strong>符合 Annex 16</strong>：按照 EU GMP 指南进行批次放行认证。</li><li><strong>EU 进口清关</strong>：为非欧盟制造商处理进口事宜。</li><li><strong>MAH 服务</strong>：为在欧盟没有实体的公司担任上市许可持有人。</li></ul>",
    ],
    whyTitle: "为什么 QP 批次放行至关重要？",
    whyBenefits: [
      { title: "法规合规", text: "满足严格的 EMA 和 EU GMP 指南要求，包括 Annex 16。" },
      { title: "产品安全", text: "确认药品的质量与安全性。" },
      { title: "市场准入", text: "促进在欧洲经济区 (EEA) 内进口与放行。" },
      { title: "临床试验准备", text: "为临床研究用药品 (IMP) 提供认证。" },
      { title: "运营保障", text: "降低不合规风险，确保运营顺畅。" },
    ],
    helpCards: {
      heading: "QbD Group 如何为您提供帮助？",
      intro: "QbD Group 提供全面的 QP 批次放行服务：",
      cards: [
        { title: "GMP 批次检测", text: "分析方法转移 (AMT)、验证 (AMV) 以及针对临床或商业化批次的检测。" },
        { title: "QP 声明", text: "为 IMP 和上市产品签发声明。" },
        { title: "留样管理", text: "储存留样与参考样品。" },
        { title: "质量管理", text: "审计生产供应链并审查批次文件。" },
        { title: "法规服务", text: "担任 MAH 并处理 EU 进口清关。" },
        { title: "持续稳定性研究", text: "提供稳定性检测以确保长期产品质量。" },
        { title: "偏差与 CAPA 管理", text: "处理超标结果、变更控制及纠正预防措施。" },
      ],
    },
    processTitle: "我们的流程",
    processIntro: "端到端的 QP 批次放行，具备法规专业知识。",
    processSteps: [
      { title: "1. 批次审查", text: "审查生产、包装和运输文件。" },
      { title: "2. GMP 检测", text: "在 GMP 条件下进行分析检测。" },
      { title: "3. QP 认证", text: "Qualified Person 证明批次符合 EU GMP 要求。" },
      { title: "4. 市场放行", text: "协助 EU 进口清关与市场放行。" },
    ],
    partnerTitle: "为什么选择 QbD Group 作为合作伙伴？",
    partnerIntro: "选择 QbD Group 进行 QP 批次放行，您将获得：",
    partnerBullets: [
      "Qualified 专家 — 内部 Qualified Person，具备深厚的法规专业知识。",
      "整合式服务 — 全面的 GMP 检测与批次放行解决方案。",
      "法规支持 — 精通 EMA 指令 2001/83/EC 和 EU GMP Annex 16 的导航。",
      "量身定制方案 — 根据您具体产品和市场需求定制的服务。",
      "全球市场准入 — 为非欧盟制造商提供在 EEA 进口和放行产品的支持。",
    ],
  },
};




/**
 * Return Spanish or Simplified Chinese i18n overrides for a given lab offering content-key.
 * Returns undefined for English or if no translation exists.
 */
export const getLabOfferingI18n = (contentKey: string, lang: SiteLang): LabOfferingI18n | undefined => {
  if (lang === "es") return labOfferingsEs[contentKey];
  if (lang === "zh-cn") return labOfferingsZh[contentKey];
  return undefined;
};
